/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

export type SpeedUnit = 'mph' | 'kph'

/**
 * Convert mph to kph
 * @param mph Speed in miles per hour
 * @returns Speed in kilometers per hour
 */
export const mphToKph = (mph: number): number => {
  return mph * 1.60934
}

/**
 * Convert kph to mph
 * @param kph Speed in kilometers per hour
 * @returns Speed in miles per hour
 */
export const kphToMph = (kph: number): number => {
  return kph / 1.60934
}

/**
 * Convert speed to the specified unit
 * @param speed Speed value in mph (base unit)
 * @param unit Target unit ('mph' or 'kph')
 * @returns Formatted speed string with unit
 */
export const formatSpeed = (speed: number | null | undefined, unit: SpeedUnit): string => {
  if (speed === null || speed === undefined || typeof speed !== 'number' || !Number.isFinite(speed)) {
    return 'N/A'
  }

  // Convert mph to kph if needed
  const value = unit === 'mph' ? kphToMph(speed) : speed

  return `${Math.round(value)} ${unit}`
}

/**
 * Convert speed from one unit to another
 * @param speed Speed value
 * @param fromUnit Source unit
 * @param toUnit Target unit
 * @returns Converted speed value
 */
export const convertSpeed = (speed: number, fromUnit: SpeedUnit, toUnit: SpeedUnit): number => {
  if (fromUnit === toUnit) return speed

  if (fromUnit === 'mph' && toUnit === 'kph') {
    return mphToKph(speed)
  } else if (fromUnit === 'kph' && toUnit === 'mph') {
    return kphToMph(speed)
  }

  return speed
}

/**
 * Convert kph speed to user's preferred units speed
 * @param {number} speed
 * @param {SpeedUnit} unit
 * @returns {number}
 */
export const kphToUser = (speed: number, unit: SpeedUnit = 'kph'): number => {
  if (unit === 'mph') {
    return Math.round(kphToMph(speed))
  }
  return speed
}

/**
 * Convert user's preferred units speed to kph speed
 * @param {number} speed
 * @param {SpeedUnit} unit
 * @returns {number}
 */
export const userToKph = (speed: number, unit: SpeedUnit = 'kph'): number => {
  if (unit === 'mph') {
    return Math.round(mphToKph(speed))
  }
  return speed
}

/**
 * Get speed unit from the user units
 * @param {'Metric' | 'Imperial'} units
 * @returns {SpeedUnit}
 */
export const getSpeedUnit = (units: 'Metric' | 'Imperial'): SpeedUnit => {
  return units === 'Metric' ? 'kph' : 'mph'
}
